import type * as React from 'react'
import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'

import type { ErrorLike } from '@apollo/client'

import { useSplitEnabled } from '@buffer-mono/features'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import type {
  ChannelAction,
  ChannelType,
  Service,
} from '~publish/pages/Community/graphqlEnums'
import { useChannelCommentCounts } from '~publish/pages/Community/hooks/comments/useChannelCommentCounts'
import { useChannelMentionCounts } from '~publish/pages/Community/hooks/mentions/useChannelMentionCounts'
import { canPerform } from '~publish/utils/permissions/canPerform'

/**
 * Fields required to check Community support for a channel. Support is
 * service/action based, so this deliberately omits `organizationId`: the
 * public `isChannelSupported` is called with org-scoped channel fragments that
 * don't carry it.
 */
type SupportCheckChannel = {
  id: string
  service: Service
  type: ChannelType
  allowedActions: ChannelAction[]
}

/**
 * Provider input shape. Adds `organizationId` so the account-wide channel list
 * (which spans every org the user belongs to) can be scoped to the current org
 * before fetching counts.
 */
type MinimalChannel = SupportCheckChannel & {
  organizationId: string
}

/**
 * Default limit for comment counts queries to prevent unbounded queries
 */
const COMMENTS_COUNT_LIMIT = 100

/**
 * Community counts context providing centralized access to comment counts
 * across all channels in the organization.
 *
 * This context:
 * - Fetches comment counts for all channels in a single API call
 * - Provides computed values for organization-wide and per-channel counts
 * - Handles real-time updates via Pusher events
 * - Only returns counts for channels that support Community feature
 *
 * @example
 * ```tsx
 * // In MainNavigation - get total count
 * const { totalOrgCount, loading } = useCommunityCountsContext()
 * return <Badge>{totalOrgCount}</Badge>
 *
 * // In ChannelSubNav - get per-channel count
 * const { getChannelCount } = useCommunityCountsContext()
 * return <Badge>{getChannelCount(channel.id)}</Badge>
 * ```
 */
export type CommunityCountsContextValue = {
  /**
   * Total count of unreplied comments across all supported channels
   */
  totalOrgCount: number

  /**
   * Get the count of unreplied comments for a specific channel
   * @param channelId - The channel ID to get count for
   * @returns The count, or null if channel is not supported or has no count
   */
  getChannelCount: (channelId: string) => number | null

  /**
   * Total count of unanswered (unreplied) mentions across all mention-supported
   * channels. Always 0 when mention counts are disabled ({@link mentionsEnabled}).
   */
  totalOrgMentionCount: number

  /**
   * Get the count of unanswered mentions for a specific channel.
   * @param channelId - The channel ID to get count for
   * @returns The count, or null if channel has no mention count / mentions are disabled
   */
  getChannelMentionCount: (channelId: string) => number | null

  /**
   * Whether mention counts are active — the counters flag and the mentions tab
   * are both enabled. When false, mention counts are not fetched and read as 0.
   */
  mentionsEnabled: boolean

  /**
   * Whether the mentions split has resolved. False while it's still loading, so
   * consumers that must not act on a not-yet-known {@link mentionsEnabled} (e.g.
   * one-shot tracking) can wait for it.
   */
  mentionsReady: boolean

  /**
   * Whether the channel supports Community feature
   * @param channel - The channel to check
   * @returns true if channel supports Community, false otherwise
   */
  isChannelSupported: (channel: SupportCheckChannel) => boolean

  /**
   * Whether counts are currently loading
   */
  loading: boolean

  /**
   * Error from fetching counts, if any
   */
  error: ErrorLike | null
}

const CommunityCountsContext = createContext<
  CommunityCountsContextValue | undefined
>(undefined)

type CommunityCountsProviderProps = {
  children: React.ReactNode
  channels: MinimalChannel[]
}

/**
 * Provider component that fetches and caches comment counts for all channels.
 *
 * Should be placed high in the component tree (e.g., in MainSidebar) to ensure
 * a single fetch provides data to all child components.
 *
 * @param props.children - Child components that will have access to counts
 * @param props.channels - Optional array of channels to fetch counts for
 */
export function CommunityCountsProvider({
  children,
  channels,
}: CommunityCountsProviderProps): JSX.Element {
  const { config } = useCommunityConfig()
  const organizationId = useOrganizationId()

  // Mention counts ride the same gate as the Mentions tab itself: where the
  // tab isn't enabled there are no mentions to count.
  const { isEnabled: isMentionsTabEnabled, isReady: isMentionsTabReady } =
    useSplitEnabled('geid-cet-mentions-tab')
  const mentionsEnabled = isMentionsTabReady && isMentionsTabEnabled

  // `channels` is the account-wide list, which spans every organization the
  // user belongs to. The counts queries are per-organization and reject channel
  // ids from another org ("bulkChannelIds spans more than one organization"), so
  // scope to the current org before fetching.
  const orgChannels = useMemo(
    () =>
      organizationId
        ? channels.filter(
            (channel) => channel.organizationId === organizationId,
          )
        : [],
    [channels, organizationId],
  )

  // Filter to only supported channels before fetching counts. MinimalChannel
  // already carries the `id` and `allowedActions` the counts query reads.
  const supportedChannels = useMemo(
    () => orgChannels.filter(config.isSupportedInComments),
    [orgChannels, config.isSupportedInComments],
  )
  // Fetch counts for all supported channels in a single query
  const { counts, loading, error } = useChannelCommentCounts({
    channels: supportedChannels,
    status: ['unreplied'],
    limit: COMMENTS_COUNT_LIMIT,
  })

  // Mentions support is service-based, but the counts query is all-or-nothing on
  // access: it errors if the list includes any channel the account can't act on.
  // Filter to viewable channels too (mentions gate on the ViewComments action,
  // same as the comments counts), mirroring useChannelCommentCounts.
  const mentionSupportedChannels = useMemo(
    () =>
      orgChannels.filter(
        (channel) =>
          config.mentions.isSupported(channel) &&
          canPerform(channel, 'viewComments'),
      ),
    [orgChannels, config.mentions],
  )
  const {
    counts: mentionCounts,
    loading: mentionsLoading,
    error: mentionsError,
  } = useChannelMentionCounts({
    channels: mentionSupportedChannels,
    skip: !mentionsEnabled,
  })

  const value = useMemo<CommunityCountsContextValue>(() => {
    // Calculate total count across all channels
    const totalOrgCount = counts
      ? counts.reduce((sum, count) => sum + count.count, 0)
      : 0

    // Function to get count for a specific channel
    const getChannelCount = (channelId: string): number | null => {
      if (!counts) return null
      const channelCount = counts.find((count) => count.channelId === channelId)
      return channelCount ? channelCount.count : null
    }

    const totalOrgMentionCount =
      mentionsEnabled && mentionCounts
        ? mentionCounts.reduce((sum, count) => sum + count.count, 0)
        : 0

    const getChannelMentionCount = (channelId: string): number | null => {
      if (!mentionsEnabled || !mentionCounts) return null
      const channelCount = mentionCounts.find(
        (count) => count.channelId === channelId,
      )
      return channelCount ? channelCount.count : null
    }

    return {
      totalOrgCount,
      getChannelCount,
      totalOrgMentionCount,
      getChannelMentionCount,
      mentionsEnabled,
      mentionsReady: isMentionsTabReady,
      isChannelSupported: config.isSupportedInComments,
      loading: loading || (mentionsEnabled && mentionsLoading),
      // Surface a failed mentions query rather than silently reading as 0.
      error: error ?? mentionsError ?? null,
    }
  }, [
    counts,
    loading,
    error,
    config.isSupportedInComments,
    mentionsEnabled,
    isMentionsTabReady,
    mentionCounts,
    mentionsLoading,
    mentionsError,
  ])

  return (
    <CommunityCountsContext.Provider value={value}>
      {children}
    </CommunityCountsContext.Provider>
  )
}

/**
 * Hook to access community counts from context.
 *
 * Must be used within a CommunityCountsProvider.
 *
 * @throws Error if used outside of CommunityCountsProvider
 * @returns CommunityCountsContextValue with counts and helper functions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { totalOrgCount, getChannelCount, loading } = useCommunityCountsContext()
 *
 *   if (loading) return <Spinner />
 *
 *   return (
 *     <>
 *       <div>Total: {totalOrgCount}</div>
 *       <div>Channel: {getChannelCount('channel-123')}</div>
 *     </>
 *   )
 * }
 * ```
 */
export function useCommunityCountsContext(): CommunityCountsContextValue {
  const context = useContext(CommunityCountsContext)

  if (!context) {
    throw new Error(
      'useCommunityCountsContext must be used within CommunityCountsProvider',
    )
  }

  return context
}

/**
 * Non-throwing variant. Returns `undefined` when rendered outside a
 * `CommunityCountsProvider`. Use it for non-critical count UI (e.g. the
 * mentions tab indicator/promo) that must still render when a Community view is
 * mounted in isolation — the provider is always present in the running app.
 */
export function useCommunityCountsContextOptional():
  | CommunityCountsContextValue
  | undefined {
  return useContext(CommunityCountsContext)
}
